// [S-404] Per-tenant MCP deployment hooks. Backed by the admin API:
//   GET    /tenants/me/mcp          → { available, deployment }
//   POST   /tenants/me/mcp/deploy   → McpDeployment
//   DELETE /tenants/me/mcp/deploy   → McpDeployment
//
// `available:false` = per-tenant deployment isn't provisioned in this
// environment yet (the deploy/undeploy buttons are hidden and the section
// explains the pending one-time GCP setup) — never a fake "deployed".
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type McpDeploymentStatus =
  | "undeployed"
  | "deploying"
  | "deployed"
  | "error";

export type McpDeployment = {
  tenantId: string;
  studioId: string;
  status: McpDeploymentStatus;
  serviceName: string | null;
  endpointUrl: string | null;
  region: string | null;
  lastDeployedAt: string | null;
  lastError: string | null;
  updatedBy: string;
  updatedAt: string;
};

export type McpDeploymentState = {
  /** False when per-tenant deployment isn't provisioned in this environment. */
  available: boolean;
  deployment: McpDeployment | null;
};

const KEY = ["mcp-deployment"] as const;

export function useMcpDeployment(
  tenantId: string
): UseQueryResult<McpDeploymentState, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...KEY, tenantId] as const,
    enabled: tenantId.length > 0,
    queryFn: () => api<McpDeploymentState>("/tenants/me/mcp"),
  });
}

export function useDeployMcp(): UseMutationResult<McpDeployment, Error, void> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<McpDeployment>("/tenants/me/mcp/deploy", { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUndeployMcp(): UseMutationResult<McpDeployment, Error, void> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<McpDeployment>("/tenants/me/mcp/deploy", { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
